import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Wallet, ArrowLeft } from 'lucide-react';

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div style={{ background: 'var(--bg-color)', color: 'var(--text-main)', minHeight: '100vh', fontFamily: 'var(--font-primary)' }}>
      
      {/* Navigation Bar */}
      <nav style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.5rem 5%', borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(10, 10, 15, 0.8)', backdropFilter: 'blur(10px)', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '3rem' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontWeight: 'bold', fontSize: '1.5rem', cursor: 'pointer', textDecoration: 'none', color: 'inherit' }}>
            <Wallet size={28} color="var(--primary)" />
            <span>Thread Finance</span>
          </Link>
        </div>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <Link to="/" className="btn btn-glass" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <ArrowLeft size={18} /> Back to Home
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main style={{ maxWidth: '800px', margin: '0 auto', padding: '4rem 5%', lineHeight: '1.7' }}>
        <h1 style={{ fontSize: '3rem', marginBottom: '1rem', color: '#fff' }}>Privacy Policy</h1>
        <p className="text-muted" style={{ marginBottom: '3rem' }}>Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>

        <section style={{ marginBottom: '3rem' }}>
          <h2 style={{ color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '0.5rem', marginBottom: '1.5rem' }}>1. Introduction</h2>
          <p>
            Welcome to Thread Finance. We respect your privacy and are committed to protecting your personal data. This privacy policy will inform you as to how we look after your personal data when you visit our website and tell you about your privacy rights and how the law protects you.
          </p>
          <p>
            <strong>Crucially, Thread Finance is a read-only Personal Financial Management (PFM) tool. We do not hold, move, or transmit real money on your behalf.</strong>
          </p>
        </section>

        <section style={{ marginBottom: '3rem' }}>
          <h2 style={{ color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '0.5rem', marginBottom: '1.5rem' }}>2. Data We Collect and Why</h2>
          <p>We believe in data minimization. We only collect the data absolutely necessary to provide our services:</p>
          <ul style={{ paddingLeft: '1.5rem', marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <li><strong>Identity Data:</strong> Name, email address, and encrypted passwords for authentication.</li>
            <li><strong>Financial Data (Read-Only):</strong> Through our secure integration with Plaid, we access read-only transaction history (typically 90 days) and deposit data. This allows us to auto-detect your recurring bills and income sources to set up your virtual envelope system.</li>
            <li><strong>Usage Data:</strong> Information about how you use our application to help us improve the experience.</li>
          </ul>
          <p>
            <strong>We never see or store your bank login credentials.</strong> You provide these directly to your bank via Plaid's secure portal. We only store encrypted OAuth tokens that grant us read-only access.
          </p>
        </section>

        <section style={{ marginBottom: '3rem' }}>
          <h2 style={{ color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '0.5rem', marginBottom: '1.5rem' }}>3. How We Use Your Data</h2>
          <p>We use your data solely for the following purposes:</p>
          <ul style={{ paddingLeft: '1.5rem', marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <li>To provide and maintain our Service, including detecting bills and paychecks.</li>
            <li>To notify you about changes to our Service.</li>
            <li>To provide customer support.</li>
            <li>To gather analysis or valuable information so that we can improve our Service.</li>
          </ul>
          <p><strong>We do not, and will never, sell your personal or financial data to third parties.</strong></p>
        </section>

        <section style={{ marginBottom: '3rem' }}>
          <h2 style={{ color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '0.5rem', marginBottom: '1.5rem' }}>4. Data Security & Deletion</h2>
          <p>
            We have put in place appropriate security measures to prevent your personal data from being accidentally lost, used, or accessed in an unauthorized way, altered, or disclosed. All data is encrypted in transit and at rest.
          </p>
          <p>
            <strong>Right to Deletion:</strong> You have full control over your data. If you choose to delete your Thread Finance account, we will permanently delete your Plaid Access Tokens, sever the connection with your bank via Plaid's systems, and purge all synced transaction history from our databases.
          </p>
        </section>

        <section style={{ marginBottom: '3rem' }}>
          <h2 style={{ color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '0.5rem', marginBottom: '1.5rem' }}>5. Third-Party Services</h2>
          <p>
            We use Plaid Inc. ("Plaid") to gather your data from financial institutions. By using our service, you grant us and Plaid the right, power, and authority to act on your behalf to access and transmit your personal and financial information from the relevant financial institution. You agree to your personal and financial information being transferred, stored, and processed by Plaid in accordance with the <a href="https://plaid.com/legal/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>Plaid Privacy Policy</a>.
          </p>
        </section>

        <section style={{ marginBottom: '3rem' }}>
          <h2 style={{ color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '0.5rem', marginBottom: '1.5rem' }}>6. Contact Us</h2>
          <p>
            If you have any questions about this privacy policy or our privacy practices, please contact us at support@threadfinance.com.
          </p>
        </section>
      </main>

      {/* Footer */}
      <footer style={{ padding: '3rem 5%', borderTop: '1px solid rgba(255,255,255,0.05)', textAlign: 'center', color: 'var(--text-muted)' }}>
        <p>&copy; {new Date().getFullYear()} Thread Finance. All rights reserved.</p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', marginTop: '1rem' }}>
          <Link to="/privacy" style={{ color: 'inherit', textDecoration: 'none' }}>Privacy Policy</Link>
          <Link to="/terms" style={{ color: 'inherit', textDecoration: 'none' }}>Terms of Service</Link>
        </div>
      </footer>
    </div>
  );
}
